
import Vue from 'vue';
import { mapActions, mapGetters, mapState } from 'vuex';
import BigNumber from 'bignumber.js';
import { calcRdBalanceToRunnitTokens, formatCooldownTimer, getCooldownTimeBucketShort, getCooldownChipLabel, SESSION_STATE } from '@run-diffusion/shared';
import moment from 'moment-timezone';
import { functions } from '@/firebase';
import BalanceCard from '@/components/BalanceCard/BalanceCard.vue';
import TimeYourSessionInputs from '@/components/TimeYourSessionInputs.vue';
import { RUNNITS_OWNER_SELECTION } from '@/views/Runnits/constants';
import ExtendOfferPriceCard from '@/components/Toolbar/SessionTimer/ExtendOfferPriceCard.vue';
import SessionSoundsSettings from '@/components/SessionSoundsSettings.vue';
import HowToStopEarlyNotice from '@/components/HowToStopEarlyNotice.vue';
import ActionsIsland from '@/components/ActionsIsland.vue';
import CooldownProgressMenu from '@/views/Runnits/CooldownProgressMenu.vue';
import CooldownLabelButton from '@/components/CooldownLabelButton.vue';
import { TeamUserMixin } from '@/mixins';

export default Vue.extend({
	name: 'ExtendSessionTimerDialog',
	mixins: [TeamUserMixin],
	props: {
		countDownTimerVal: { type: String, required: true },
		session: { type: Object, required: true },
	},
	data () {
		return {
			RUNNITS_OWNER_SELECTION,
			dialogOpen: false,
			extending: false,
			selectedScheduledStopInHrs: 0,
			selectedScheduledStopInMins: 0,
		};
	},
	watch: {
		dialogOpen (isOpen: boolean) {
			if (isOpen && this.isTeamSessionForCooldown && this.hasTokenCooldownPermission && this.team?.id) {
				this.fetchTokenCooldownStatus();
			}
		},
		shouldRefetchTokenCooldown (shouldRefetch: boolean) {
			if (shouldRefetch && this.isTeamSessionForCooldown && this.hasTokenCooldownPermission && this.team?.id) {
				this.fetchTokenCooldownStatus();
			}
		},
	},
	computed: {
		...mapGetters([
			'companyClubOfferBenefits',
		]),
		...mapState([
			'team',
			'teamBalanceAccount',
			'runnitState',
			'realTimeMoment',
		]),
		canGoNegative () {
			return !!(
				this.session?.teamId &&
				this.teamBalanceAccount?.type === 'TEAM' &&
				this.companyClubOfferBenefits.isEnterprise &&
				!this.team.allotmentSettings?.isBoundToBudget
			);
		},
		confirmDisabled () {
			return !!(
				(!this.selectedScheduledStopInHrs && !this.selectedScheduledStopInMins) ||
				this.extending ||
				this.tokenCooldownBlocksExtend
			);
		},
		extendMins () {
			if (
				this.selectedScheduledStopInHrs ||
				this.selectedScheduledStopInMins
			) {
				return (this.selectedScheduledStopInHrs * 60) + this.selectedScheduledStopInMins;
			}
			return 0;
		},
		isTeamSessionForCooldown () {
			return !!(this.session?.teamId && this.team?.id);
		},
		estimatedExtendTokenCostInHundredths () {
			if (!this.extendMins || !this.session?.purchasePrice) return 0;
			const costCents = new BigNumber(this.extendMins).div(60).times(this.session.purchasePrice).integerValue(BigNumber.ROUND_HALF_UP).toNumber();
			return calcRdBalanceToRunnitTokens(costCents, true);
		},
		extendCooldownData () {
			return this.runnitState?.tokenCooldownData?.cooldownData ?? null;
		},
		cooldownEndsAt () {
			return this.runnitState?.tokenCooldownData?.cooldownEndsAt ?? null;
		},
		shouldRefetchTokenCooldown () {
			const endsAt = this.cooldownEndsAt;
			if (!endsAt || !this.realTimeMoment) return false;
			return moment(endsAt).isSameOrBefore(this.realTimeMoment);
		},
		tokenCooldownBlocksExtend () {
			if (!this.isTeamSessionForCooldown || !this.hasTokenCooldownPermission) return false;
			const cooldownData = this.extendCooldownData;
			if (!cooldownData || typeof cooldownData.tokensRemaining !== 'number') return false;
			return cooldownData.tokensRemaining < this.estimatedExtendTokenCostInHundredths;
		},
		extendCooldownChipTimerText () {
			if (!this.cooldownEndsAt || !this.realTimeMoment) return '';
			const resetAtMoment = moment(this.cooldownEndsAt);
			const totalSeconds = Math.max(0, resetAtMoment.diff(this.realTimeMoment, 'seconds'));
			const bucketShort = getCooldownTimeBucketShort(this.extendCooldownData?.timeBucket);
			return formatCooldownTimer(totalSeconds, bucketShort);
		},
		extendCooldownChipLabel () {
			if (!this.tokenCooldownBlocksExtend || !this.extendCooldownData) return '';
			return getCooldownChipLabel(
				this.estimatedExtendTokenCostInHundredths,
				this.extendCooldownData.threshold,
				this.extendCooldownChipTimerText
			);
		},
	},
	methods: {
		...mapActions([
			'extendSession',
			'updateTokenCooldownData',
		]),
		onCancel () {
			this.dialogOpen = false;
		},
		openExtendCooldownMenu () {
			this.$nextTick(() => {
				const menu = this.$refs.extendCooldownMenu as { openMenu?: () => void };
				if (menu?.openMenu) menu.openMenu();
			});
		},
		async fetchTokenCooldownStatus () {
			if (!this.team?.id) return;
			try {
				const getTokenCooldownStatusFn = functions.httpsCallable('getTokenCooldownStatus');
				const { data } = await getTokenCooldownStatusFn({ teamId: this.team.id });
				if (!data?.success) return;
				if (data.resetAt != null) {
					this.updateTokenCooldownData({
						cooldownEndsAt: new Date(data.resetAt),
						cooldownData: {
							tokensRemaining: data.tokensRemaining,
							threshold: data.threshold,
							resetAt: data.resetAt,
							timeBucket: data.timeBucket,
							tokensSpent: data.tokensSpent,
						},
					});
				} else {
					this.updateTokenCooldownData({
						cooldownEndsAt: null,
						cooldownData: null,
					});
				}
			} catch (e) {
				console.error('fetchTokenCooldownStatus', e);
			}
		},
		async onConfirm () {
			if (
				this.session.state !== SESSION_STATE.READY ||
				this.confirmDisabled
			) {
				return;
			}

			this.extending = true;
			const { success } = await this.extendSession({
				session: this.session,
				extendMins: this.extendMins,
			});

			if (success) {
				this.selectedScheduledStopInHrs = 0;
				this.selectedScheduledStopInMins = 0;
				this.onCancel();
			}

			this.extending = false;
		},
	},
	components: {
		ActionsIsland,
		HowToStopEarlyNotice,
		SessionSoundsSettings,
		ExtendOfferPriceCard,
		TimeYourSessionInputs,
		BalanceCard,
		CooldownProgressMenu,
		CooldownLabelButton,
	},
});
